import { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Button, Input, Table } from 'react-daisyui';
import { QBODetailedTransaction } from '@shared/types/qbo_data';
import { getPageRecords } from '@/utils';
import { put } from '@/utils/queries';
import { alertErrorMessage, alertMessageSuccess } from '@/utils/alerts';
import { DashboardCard } from '@/components/DashboardCard';
import { Spinner } from '@/components/Spinner';
import { OSPagination } from '@/components/Pagination/Pagination';
import { SortArrow } from '@/components/SortArrow/SortArrow';
import { useTableSort } from '@/components/Table/useTableSort';
import { formatCategoryLabel } from '@/pages/Transactions/categoryLabel';
import {
  CategoryPicker,
  ClipText,
  ClipValue,
  formatDate,
  getAmount,
  usdFormatter,
} from './transactionsComponents';
import './TableTransactionsCompact.css';

function ClientRow({
  transaction,
  onConfirm,
}: {
  transaction: QBODetailedTransaction;
  onConfirm: (
    t: QBODetailedTransaction
  ) => Promise<QBODetailedTransaction | null>;
}) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modified, setModified] = useState<QBODetailedTransaction>(transaction);
  const amount = getAmount(transaction);

  const isUpdated =
    !!modified.modified_memo || !!modified.modified_qbo_categories_id;

  const onConfirmClick = async () => {
    if (!isUpdated) return;
    const updated = await onConfirm(modified);
    if (updated) {
      setModified(updated);
      setIsOpen(false);
    }
  };

  const originalCard = (
    <div className='grid min-w-0 gap-2 rounded-lg border border-base-content/10 p-3'>
      <div className='cmp-title'>Original (QBO)</div>
      <div className='cmp-kv'>
        <span className='cmp-label'>Memo</span>
        <ClipValue value={transaction.memo} />
      </div>
      <div className='cmp-kv'>
        <span className='cmp-label'>Category</span>
        <ClipValue value={formatCategoryLabel(transaction.qbo_category)} />
      </div>
    </div>
  );

  const memoInput = (
    <Input
      size='sm'
      className='w-full'
      placeholder='Set a memo'
      value={modified.modified_memo ?? ''}
      onChange={(e) =>
        setModified({ ...modified, modified_memo: e.target.value })
      }
    />
  );

  const categoryPicker = (
    <CategoryPicker
      transaction={transaction}
      value={
        modified.modified_qbo_categories_id
          ? formatCategoryLabel(modified.modified_qbo_category)
          : undefined
      }
      onSelected={(category) =>
        setModified({
          ...modified,
          modified_qbo_categories_id: category?.id || undefined,
          modified_qbo_category: {
            name: category?.name || '',
            external_id: category?.external_id,
          },
        })
      }
    />
  );

  const confirmButton = (
    <Button
      size='sm'
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      data-testid={`client-confirm-${transaction.id}`}
      className={`btn-accent ${isUpdated ? '' : 'btn-disabled'}`}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={onConfirmClick}
    >
      Confirm
    </Button>
  );

  return (
    <>
      <tr
        className='cursor-pointer'
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        data-testid={`client-tr-${transaction.id}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <td className='w-[34px] text-center'>
          <FontAwesomeIcon
            icon={isUpdated ? faCheck : faChevronRight}
            className={`transition-transform ${
              isOpen && !isUpdated ? 'rotate-90' : ''
            } ${isUpdated ? 'text-secondary' : 'text-base-content/50'}`}
          />
        </td>
        <td>
          <ClipText value={transaction.payee} width={150} />
        </td>
        <td>
          <ClipText value={transaction.memo} width={240} />
        </td>
        <td
          className={`tabular-nums font-semibold whitespace-nowrap text-right ${
            amount >= 0 ? 'text-green-500' : 'text-red-500'
          }`}
        >
          {usdFormatter.format(amount)}
        </td>
        <td className='whitespace-nowrap'>{transaction.bank || '—'}</td>
        <td className='whitespace-nowrap'>{formatDate(transaction.date)}</td>
      </tr>
      {isOpen ? (
        <tr>
          <td colSpan={6} className='cmp-detail-cell'>
            <div className='grid gap-3.5 rounded-xl border border-base-content/20 bg-base-content/5 p-3.5'>
              <div className='grid grid-cols-1 gap-3.5 sm:grid-cols-2'>
                {originalCard}
                <div className='grid min-w-0 gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3'>
                  <div className='cmp-title cmp-title--client'>
                    Your response
                  </div>
                  <div className='grid gap-1'>
                    <span className='cmp-label'>Memo</span>
                    {memoInput}
                  </div>
                  <div className='cmp-category grid gap-1'>
                    <span className='cmp-label'>Category</span>
                    {categoryPicker}
                  </div>
                </div>
              </div>
              <div className='flex justify-end'>{confirmButton}</div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function TableTransactionsClient({
  transactions,
}: {
  transactions: QBODetailedTransaction[];
}) {
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { sort, sortBy, sortData } = useTableSort<QBODetailedTransaction>({
    field: 'payee',
    asc: true,
  });

  useEffect(() => {
    setSelectedPage(1);
  }, [recordsPerPage, transactions, sort]);

  const pageRecords = getPageRecords(
    sortData(transactions),
    selectedPage,
    recordsPerPage
  ) as QBODetailedTransaction[];

  const updateTransaction = async (transaction: QBODetailedTransaction) => {
    setIsSaving(true);
    const body = {
      client_id: transaction.client_id,
      modified_qbo_categories_id:
        transaction.modified_qbo_categories_id || undefined,
      modified_memo: transaction.modified_memo,
    };
    try {
      let updatedTransaction = await put<QBODetailedTransaction>(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `transactions/${transaction.id}/response`,
        body
      );
      updatedTransaction = {
        ...updatedTransaction,
        modified_qbo_category: transaction.modified_qbo_category,
        qbo_category: transaction.qbo_category,
      };
      const index = transactions.findIndex(
        (tr) => tr.id === updatedTransaction.id
      );
      // eslint-disable-next-line no-param-reassign
      if (index !== -1) transactions[index] = updatedTransaction;
      alertMessageSuccess('Transaction updated successfully.');
      return updatedTransaction;
    } catch (error) {
      alertErrorMessage('Error while updating a transaction, try again later.');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const headerCell = (label: string, field?: keyof QBODetailedTransaction) => (
    <div
      role='button'
      tabIndex={0}
      className={field ? 'cursor-pointer' : 'pointer-events-none'}
      onClick={() => field && sortBy(field)}
    >
      <span className='text-base font-bold capitalize'>
        {label}
        {field ? <SortArrow sort={sort} fieldName={field} /> : null}
      </span>
    </div>
  );

  return (
    <DashboardCard className='mt-0 p-0 min-h-fit' cardBodyclassName='p-0'>
      <ToastContainer />
      {isSaving ? (
        <div
          className='fixed inset-0 z-[999] flex items-center justify-center bg-black/50'
          data-testid='spinner-transaction'
        >
          <Spinner className='h-1/4' />
        </div>
      ) : null}
      <Table
        zebra
        id='table-transactions-compact'
        data-testid='transactions-client'
      >
        <Table.Head>
          <span aria-label='expand' />
          {headerCell('Payee', 'payee')}
          {headerCell('Memo', 'memo')}
          {headerCell('Amount')}
          {headerCell('Bank', 'bank')}
          {headerCell('Date', 'date')}
        </Table.Head>
        <tbody>
          {pageRecords.map((t) => (
            <ClientRow
              key={`client-${t.txn_id}-${t.qbo_company_id as number}`}
              transaction={t}
              onConfirm={updateTransaction}
            />
          ))}
        </tbody>
      </Table>
      <OSPagination
        totalRecords={transactions.length}
        numPages={Math.ceil(transactions.length / recordsPerPage)}
        selectedPage={selectedPage}
        rowsPerPage={recordsPerPage}
        selectedRowsPerPage={[10, 20, 30, 40, 50]}
        onPageSelect={setSelectedPage}
        onSelectRowsChange={setRecordsPerPage}
      />
    </DashboardCard>
  );
}

export default TableTransactionsClient;
export { TableTransactionsClient };
